import { Type as t } from '@sinclair/typebox';
import { Locale as L, type Locale } from '../i18n/locale';

export const Category = {
  Animals: 'animals',
  Food: 'food',
  Objects: 'objects',
  Geography: 'geography',
  Nature: 'nature',
  Sports: 'sports',
  Movies: 'movies',
  Slang: 'slang',
} as const;
export type Category = (typeof Category)[keyof typeof Category];

export const ALL_CATEGORIES: readonly Category[] = Object.values(Category);

export const CategorySchema = t.Union(
  [
    t.Literal(Category.Animals),
    t.Literal(Category.Food),
    t.Literal(Category.Objects),
    t.Literal(Category.Geography),
    t.Literal(Category.Nature),
    t.Literal(Category.Sports),
    t.Literal(Category.Movies),
    t.Literal(Category.Slang),
  ],
  { $id: 'Category' },
);

/** A `[flic, infiltre]` pair shown to the two sides during a round. */
export type WordPair = readonly [flic: string, infiltre: string];

type CategoryContent = Readonly<Record<Locale, readonly WordPair[]>>;

/** Word-pair pool indexed as `Category → Locale → WordPair[]`. */
export const CATEGORIES: Readonly<Record<Category, CategoryContent>> = {
  [Category.Animals]: {
    [L.EN]: [
      ['cat', 'lion'],
      ['dog', 'wolf'],
      ['tiger', 'panther'],
      ['mouse', 'rat'],
      ['shark', 'dolphin'],
      ['dolphin', 'whale'],
      ['octopus', 'squid'],
      ['eagle', 'hawk'],
      ['crow', 'raven'],
      ['parrot', 'cockatoo'],
      ['crocodile', 'alligator'],
      ['giraffe', 'zebra'],
      ['elephant', 'rhino'],
      ['bee', 'wasp'],
      ['ant', 'termite'],
      ['butterfly', 'moth'],
      ['snail', 'slug'],
      ['rabbit', 'hare'],
      ['horse', 'donkey'],
      ['cow', 'bull'],
      ['sheep', 'goat'],
      ['pig', 'boar'],
      ['duck', 'goose'],
      ['chicken', 'turkey'],
      ['frog', 'toad'],
      ['snake', 'viper'],
      ['lizard', 'gecko'],
      ['bear', 'panda'],
      ['gorilla', 'chimpanzee'],
      ['kangaroo', 'wallaby'],
      ['penguin', 'puffin'],
      ['shark', 'orca'],
      ['jellyfish', 'anemone'],
      ['crab', 'lobster'],
      ['shrimp', 'prawn'],
      ['mosquito', 'fly'],
      ['spider', 'scorpion'],
      ['ladybug', 'beetle'],
      ['hedgehog', 'squirrel'],
      ['weasel', 'ferret'],
      ['badger', 'otter'],
      ['beaver', 'muskrat'],
      ['fox', 'jackal'],
      ['coyote', 'hyena'],
      ['lynx', 'bobcat'],
      ['bison', 'buffalo'],
      ['moose', 'elk'],
      ['deer', 'doe'],
      ['trout', 'salmon'],
      ['owl', 'falcon'],
    ],
    [L.FR]: [
      ['chien', 'chat'],
      ['chien', 'loup'],
      ['chat', 'lion'],
      ['tigre', 'panthère'],
      ['souris', 'rat'],
      ['loup', 'renard'],
      ['requin', 'dauphin'],
      ['dauphin', 'baleine'],
      ['méduse', 'poulpe'],
      ['poulpe', 'calamar'],
      ['pingouin', 'mouette'],
      ['perroquet', 'corbeau'],
      ['chauve-souris', 'hibou'],
      ['aigle', 'faucon'],
      ['hibou', 'chouette'],
      ['corbeau', 'corneille'],
      ['alligator', 'crocodile'],
      ['girafe', 'zèbre'],
      ['éléphant', 'rhinocéros'],
      ['huître', 'palourde'],
      ['abeille', 'guêpe'],
      ['papillon', 'mite'],
      ['escargot', 'limace'],
      ['lapin', 'lièvre'],
      ['cheval', 'âne'],
      ['vache', 'taureau'],
      ['mouton', 'chèvre'],
      ['cochon', 'sanglier'],
      ['canard', 'oie'],
      ['poule', 'dinde'],
      ['grenouille', 'crapaud'],
      ['serpent', 'vipère'],
      ['lézard', 'gecko'],
      ['ours', 'panda'],
      ['gorille', 'chimpanzé'],
      ['kangourou', 'wallaby'],
      ['léopard', 'guépard'],
      ['crabe', 'homard'],
      ['crevette', 'langoustine'],
      ['moustique', 'mouche'],
      ['araignée', 'scorpion'],
      ['coccinelle', 'scarabée'],
      ['hérisson', 'écureuil'],
      ['renard', 'chacal'],
      ['coyote', 'hyène'],
      ['truite', 'saumon'],
      ['bison', 'buffle'],
      ['virus', 'bactérie'],
      ['dragon', 'dinosaure'],
      ['fantôme', 'zombie'],
    ],
    [L.ES]: [
      ['gato', 'león'],
      ['perro', 'lobo'],
      ['tigre', 'pantera'],
      ['ratón', 'rata'],
      ['tiburón', 'delfín'],
      ['delfín', 'ballena'],
      ['pulpo', 'calamar'],
      ['águila', 'halcón'],
      ['cuervo', 'grajo'],
      ['lechuza', 'búho'],
      ['cocodrilo', 'caimán'],
      ['jirafa', 'cebra'],
      ['elefante', 'rinoceronte'],
      ['abeja', 'avispa'],
      ['hormiga', 'termita'],
      ['mariposa', 'polilla'],
      ['caracol', 'babosa'],
      ['conejo', 'liebre'],
      ['caballo', 'burro'],
      ['vaca', 'toro'],
      ['oveja', 'cabra'],
      ['cerdo', 'jabalí'],
      ['pato', 'oca'],
      ['gallina', 'pavo'],
      ['rana', 'sapo'],
      ['serpiente', 'víbora'],
      ['lagarto', 'gecko'],
      ['oso', 'panda'],
      ['gorila', 'chimpancé'],
      ['canguro', 'ualabí'],
      ['loro', 'cacatúa'],
      ['pingüino', 'frailecillo'],
      ['tiburón', 'orca'],
      ['medusa', 'anémona'],
      ['cangrejo', 'langosta'],
      ['gamba', 'langostino'],
      ['mosquito', 'mosca'],
      ['araña', 'escorpión'],
      ['mariquita', 'escarabajo'],
      ['erizo', 'ardilla'],
      ['comadreja', 'hurón'],
      ['tejón', 'nutria'],
      ['castor', 'coipo'],
      ['zorro', 'chacal'],
      ['coyote', 'hiena'],
      ['lince', 'gato montés'],
      ['bisonte', 'búfalo'],
      ['alce', 'ciervo'],
      ['corzo', 'cierva'],
      ['trucha', 'salmón'],
    ],
    [L.DE]: [
      ['Katze', 'Löwe'],
      ['Hund', 'Wolf'],
      ['Tiger', 'Panther'],
      ['Maus', 'Ratte'],
      ['Hai', 'Delfin'],
      ['Delfin', 'Wal'],
      ['Krake', 'Tintenfisch'],
      ['Adler', 'Falke'],
      ['Krähe', 'Rabe'],
      ['Eule', 'Uhu'],
      ['Krokodil', 'Alligator'],
      ['Giraffe', 'Zebra'],
      ['Elefant', 'Nashorn'],
      ['Biene', 'Wespe'],
      ['Ameise', 'Termite'],
      ['Schmetterling', 'Motte'],
      ['Schnecke', 'Nacktschnecke'],
      ['Kaninchen', 'Hase'],
      ['Pferd', 'Esel'],
      ['Kuh', 'Stier'],
      ['Schaf', 'Ziege'],
      ['Schwein', 'Wildschwein'],
      ['Ente', 'Gans'],
      ['Huhn', 'Pute'],
      ['Frosch', 'Kröte'],
      ['Schlange', 'Viper'],
      ['Eidechse', 'Gecko'],
      ['Bär', 'Panda'],
      ['Gorilla', 'Schimpanse'],
      ['Känguru', 'Wallaby'],
      ['Papagei', 'Kakadu'],
      ['Pinguin', 'Papageitaucher'],
      ['Hai', 'Orca'],
      ['Qualle', 'Anemone'],
      ['Krabbe', 'Hummer'],
      ['Garnele', 'Languste'],
      ['Mücke', 'Fliege'],
      ['Spinne', 'Skorpion'],
      ['Marienkäfer', 'Käfer'],
      ['Igel', 'Eichhörnchen'],
      ['Wiesel', 'Frettchen'],
      ['Dachs', 'Otter'],
      ['Biber', 'Bisamratte'],
      ['Fuchs', 'Schakal'],
      ['Kojote', 'Hyäne'],
      ['Luchs', 'Wildkatze'],
      ['Bison', 'Büffel'],
      ['Elch', 'Hirsch'],
      ['Reh', 'Hirschkuh'],
      ['Forelle', 'Lachs'],
    ],
    [L.PT]: [
      ['gato', 'leão'],
      ['cão', 'lobo'],
      ['tigre', 'pantera'],
      ['rato', 'ratazana'],
      ['tubarão', 'golfinho'],
      ['golfinho', 'baleia'],
      ['polvo', 'lula'],
      ['águia', 'falcão'],
      ['corvo', 'gralha'],
      ['coruja', 'mocho'],
      ['crocodilo', 'aligátor'],
      ['girafa', 'zebra'],
      ['elefante', 'rinoceronte'],
      ['abelha', 'vespa'],
      ['formiga', 'térmita'],
      ['borboleta', 'traça'],
      ['caracol', 'lesma'],
      ['coelho', 'lebre'],
      ['cavalo', 'burro'],
      ['vaca', 'touro'],
      ['ovelha', 'cabra'],
      ['porco', 'javali'],
      ['pato', 'ganso'],
      ['galinha', 'peru'],
      ['rã', 'sapo'],
      ['cobra', 'víbora'],
      ['lagarto', 'osga'],
      ['urso', 'panda'],
      ['gorila', 'chimpanzé'],
      ['canguru', 'wallaby'],
      ['papagaio', 'catatua'],
      ['pinguim', 'papagaio-do-mar'],
      ['tubarão', 'orca'],
      ['medusa', 'anémona'],
      ['caranguejo', 'lavagante'],
      ['camarão', 'lagostim'],
      ['mosquito', 'mosca'],
      ['aranha', 'escorpião'],
      ['joaninha', 'escaravelho'],
      ['ouriço', 'esquilo'],
      ['doninha', 'furão'],
      ['texugo', 'lontra'],
      ['castor', 'ratão'],
      ['raposa', 'chacal'],
      ['coiote', 'hiena'],
      ['lince', 'gato bravo'],
      ['bisonte', 'búfalo'],
      ['alce', 'veado'],
      ['corço', 'corça'],
      ['truta', 'salmão'],
    ],
  },
  [Category.Food]: {
    [L.EN]: [
      ['coffee', 'tea'],
      ['beer', 'wine'],
      ['whisky', 'vodka'],
      ['champagne', 'prosecco'],
      ['milk', 'yogurt'],
      ['water', 'soda'],
      ['coke', 'pepsi'],
      ['pizza', 'lasagna'],
      ['burger', 'sandwich'],
      ['bread', 'brioche'],
      ['baguette', 'ciabatta'],
      ['croissant', 'pain au chocolat'],
      ['pancake', 'waffle'],
      ['cookie', 'brownie'],
      ['cheddar', 'gouda'],
      ['fondue', 'raclette'],
      ['stew', 'casserole'],
      ['pasta', 'rice'],
      ['fries', 'chips'],
      ['apple', 'pear'],
      ['strawberry', 'raspberry'],
      ['watermelon', 'melon'],
      ['carrot', 'beetroot'],
      ['zucchini', 'cucumber'],
      ['onion', 'shallot'],
      ['salt', 'sugar'],
      ['mustard', 'mayonnaise'],
      ['honey', 'jam'],
      ['chocolate', 'vanilla'],
      ['ice cream', 'sorbet'],
      ['olive', 'caper'],
      ['pickle', 'gherkin'],
      ['tomato', 'pepper'],
      ['lettuce', 'kale'],
      ['spinach', 'chard'],
      ['broccoli', 'cauliflower'],
      ['mushroom', 'truffle'],
      ['garlic', 'ginger'],
      ['basil', 'parsley'],
      ['thyme', 'rosemary'],
      ['paprika', 'turmeric'],
      ['curry', 'saffron'],
      ['aioli', 'pesto'],
      ['vinegar', 'oil'],
      ['soup', 'broth'],
      ['quiche', 'tart'],
      ["shepherd's pie", 'cottage pie'],
      ['dumpling', 'ravioli'],
      ['ratatouille', 'paella'],
      ['tiramisu', 'panna cotta'],
    ],
    [L.FR]: [
      ['café', 'thé'],
      ['bière', 'vin'],
      ['whisky', 'vodka'],
      ['lait', 'jus'],
      ['eau', 'soda'],
      ['coca-cola', 'pepsi'],
      ['boisson énergisante', 'soda'],
      ['lait de coco', 'lait de soja'],
      ['pizza', 'burger'],
      ['pizza', 'lasagnes'],
      ['pizza', 'pâtes'],
      ['sushi', 'pizza'],
      ['hamburger', 'hot-dog'],
      ['hot-dog', 'bratwurst'],
      ['tortilla', 'taco'],
      ['baguette', 'ciabatta'],
      ['pain', 'beurre'],
      ['pain', 'toast'],
      ['crêpe', 'beignet'],
      ['glace', 'gâteau'],
      ['chocolat', 'vanille'],
      ['riz', 'pâtes'],
      ['poulet', 'bœuf'],
      ['pomme', 'orange'],
      ['pomme', 'poire'],
      ['banane', 'pomme'],
      ['banane', 'fraise'],
      ['banane', 'plantain'],
      ['fraise', 'framboise'],
      ['concombre', 'courgette'],
      ['carotte', 'brocoli'],
      ['pomme de terre', 'tomate'],
      ['noix de coco', 'ananas'],
      ['oignon', 'ail'],
      ['sel', 'poivre'],
      ['sel', 'sucre'],
      ['sucre', 'miel'],
      ['McDonald’s', 'KFC'],
      ['Burger King', 'McDonald’s'],
      ['camembert', 'brie'],
      ['raclette', 'fondue'],
      ['croissant', 'pain au chocolat'],
      ['ratatouille', 'piperade'],
      ['cassoulet', 'choucroute'],
      ['macaron', 'madeleine'],
      ['tartiflette', 'gratin'],
      ['blanquette', 'pot-au-feu'],
      ['frites', 'chips'],
      ['moutarde', 'mayonnaise'],
      ['glace', 'sorbet'],
    ],
    [L.ES]: [
      ['café', 'té'],
      ['cerveza', 'vino'],
      ['whisky', 'vodka'],
      ['champán', 'cava'],
      ['leche', 'yogur'],
      ['agua', 'refresco'],
      ['cola', 'pepsi'],
      ['pizza', 'lasaña'],
      ['hamburguesa', 'bocadillo'],
      ['pan', 'brioche'],
      ['baguette', 'chapata'],
      ['croissant', 'napolitana'],
      ['crepe', 'gofre'],
      ['galleta', 'brownie'],
      ['queso manchego', 'queso curado'],
      ['paella', 'fideuá'],
      ['cocido', 'guiso'],
      ['pasta', 'arroz'],
      ['patatas fritas', 'chips'],
      ['manzana', 'pera'],
      ['fresa', 'frambuesa'],
      ['sandía', 'melón'],
      ['zanahoria', 'remolacha'],
      ['calabacín', 'pepino'],
      ['cebolla', 'chalota'],
      ['sal', 'azúcar'],
      ['mostaza', 'mayonesa'],
      ['miel', 'mermelada'],
      ['chocolate', 'vainilla'],
      ['helado', 'sorbete'],
      ['aceituna', 'alcaparra'],
      ['pepinillo', 'pickle'],
      ['tomate', 'pimiento'],
      ['lechuga', 'espinaca'],
      ['rúcula', 'canónigos'],
      ['brócoli', 'coliflor'],
      ['seta', 'trufa'],
      ['ajo', 'jengibre'],
      ['albahaca', 'perejil'],
      ['tomillo', 'romero'],
      ['pimentón', 'cúrcuma'],
      ['curry', 'azafrán'],
      ['alioli', 'pesto'],
      ['vinagre', 'aceite'],
      ['sopa', 'caldo'],
      ['quiche', 'tarta'],
      ['tortilla', 'frittata'],
      ['gazpacho', 'salmorejo'],
      ['paella', 'arroz a banda'],
      ['tiramisú', 'panna cotta'],
    ],
    [L.DE]: [
      ['Kaffee', 'Tee'],
      ['Bier', 'Wein'],
      ['Whisky', 'Wodka'],
      ['Champagner', 'Sekt'],
      ['Milch', 'Joghurt'],
      ['Wasser', 'Limonade'],
      ['Cola', 'Pepsi'],
      ['Pizza', 'Lasagne'],
      ['Burger', 'Sandwich'],
      ['Brot', 'Brioche'],
      ['Baguette', 'Ciabatta'],
      ['Croissant', 'Plunder'],
      ['Pfannkuchen', 'Waffel'],
      ['Keks', 'Brownie'],
      ['Cheddar', 'Gouda'],
      ['Fondue', 'Raclette'],
      ['Eintopf', 'Gulasch'],
      ['Nudeln', 'Reis'],
      ['Pommes', 'Chips'],
      ['Apfel', 'Birne'],
      ['Erdbeere', 'Himbeere'],
      ['Wassermelone', 'Honigmelone'],
      ['Karotte', 'Rote Bete'],
      ['Zucchini', 'Gurke'],
      ['Zwiebel', 'Schalotte'],
      ['Salz', 'Zucker'],
      ['Senf', 'Mayonnaise'],
      ['Honig', 'Marmelade'],
      ['Schokolade', 'Vanille'],
      ['Eis', 'Sorbet'],
      ['Olive', 'Kaper'],
      ['Gewürzgurke', 'Cornichon'],
      ['Tomate', 'Paprika'],
      ['Salat', 'Spinat'],
      ['Spinat', 'Mangold'],
      ['Brokkoli', 'Blumenkohl'],
      ['Pilz', 'Trüffel'],
      ['Knoblauch', 'Ingwer'],
      ['Basilikum', 'Petersilie'],
      ['Thymian', 'Rosmarin'],
      ['Pfeffer', 'Kurkuma'],
      ['Curry', 'Safran'],
      ['Aioli', 'Pesto'],
      ['Essig', 'Öl'],
      ['Suppe', 'Brühe'],
      ['Quiche', 'Tarte'],
      ['Auflauf', 'Soufflé'],
      ['Sauerkraut', 'Kohlrabi'],
      ['Schnitzel', 'Rouladen'],
      ['Tiramisu', 'Panna Cotta'],
    ],
    [L.PT]: [
      ['café', 'chá'],
      ['cerveja', 'vinho'],
      ['whisky', 'vodka'],
      ['champanhe', 'espumante'],
      ['leite', 'iogurte'],
      ['água', 'refrigerante'],
      ['coca-cola', 'pepsi'],
      ['piza', 'lasanha'],
      ['hambúrguer', 'sanduíche'],
      ['pão', 'brioche'],
      ['baguete', 'chapata'],
      ['croissant', 'pão de chocolate'],
      ['crepe', 'waffle'],
      ['bolacha', 'brownie'],
      ['queijo da serra', 'queijo flamengo'],
      ['fondue', 'raclette'],
      ['cozido', 'guisado'],
      ['massa', 'arroz'],
      ['batatas fritas', 'chips'],
      ['maçã', 'pera'],
      ['morango', 'framboesa'],
      ['melancia', 'melão'],
      ['cenoura', 'beterraba'],
      ['courgette', 'pepino'],
      ['cebola', 'chalota'],
      ['sal', 'açúcar'],
      ['mostarda', 'maionese'],
      ['mel', 'compota'],
      ['chocolate', 'baunilha'],
      ['gelado', 'sorvete'],
      ['azeitona', 'alcaparra'],
      ['pepino', 'cornichão'],
      ['tomate', 'pimento'],
      ['alface', 'espinafre'],
      ['rúcula', 'agrião'],
      ['brócolos', 'couve-flor'],
      ['cogumelo', 'trufa'],
      ['alho', 'gengibre'],
      ['manjericão', 'salsa'],
      ['tomilho', 'alecrim'],
      ['paprica', 'curcuma'],
      ['caril', 'açafrão'],
      ['aioli', 'pesto'],
      ['vinagre', 'azeite'],
      ['sopa', 'caldo'],
      ['quiche', 'tarte'],
      ['bacalhau', 'polvo'],
      ['francesinha', 'bifana'],
      ['arroz de marisco', 'paelha'],
      ['tiramisu', 'panna cotta'],
    ],
  },
  [Category.Objects]: {
    [L.EN]: [
      ['phone', 'tablet'],
      ['desktop', 'laptop'],
      ['mouse', 'keyboard'],
      ['television', 'radio'],
      ['headphones', 'earbuds'],
      ['microphone', 'speaker'],
      ['oven', 'microwave'],
      ['pan', 'pot'],
      ['plate', 'bowl'],
      ['knife', 'fork'],
      ['spoon', 'ladle'],
      ['cup', 'mug'],
      ['glass', 'tumbler'],
      ['book', 'magazine'],
      ['pen', 'pencil'],
      ['scissors', 'cutter'],
      ['chair', 'stool'],
      ['table', 'desk'],
      ['couch', 'armchair'],
      ['bed', 'mattress'],
      ['wardrobe', 'dresser'],
      ['lamp', 'candle'],
      ['umbrella', 'parasol'],
      ['bag', 'backpack'],
      ['suitcase', 'briefcase'],
      ['wallet', 'purse'],
      ['key', 'padlock'],
      ['watch', 'bracelet'],
      ['glasses', 'magnifier'],
      ['mirror', 'frame'],
      ['broom', 'mop'],
      ['vacuum', 'sweeper'],
      ['washer', 'dryer'],
      ['fridge', 'freezer'],
      ['toaster', 'kettle'],
      ['coffee maker', 'teapot'],
      ['blender', 'mixer'],
      ['fan', 'AC'],
      ['heater', 'radiator'],
      ['remote', 'controller'],
      ['camera', 'camcorder'],
      ['printer', 'scanner'],
      ['speaker', 'amplifier'],
      ['cable', 'charger'],
      ['socket', 'switch'],
      ['bulb', 'neon'],
      ['nail', 'screw'],
      ['hammer', 'screwdriver'],
      ['drill', 'saw'],
      ['shovel', 'pickaxe'],
    ],
    [L.FR]: [
      ['ordinateur portable', 'tablette'],
      ['ordinateur portable', 'ordinateur de bureau'],
      ['tablette', 'smartphone'],
      ['TV', 'ordinateur portable'],
      ['télévision', 'radio'],
      ['souris', 'clavier'],
      ['casque audio', 'écouteurs'],
      ['microphone', 'haut-parleur'],
      ['microphone', 'caméra'],
      ['projecteur', 'écran'],
      ['four', 'micro-ondes'],
      ['four', 'chauffage'],
      ['poêle', 'casserole'],
      ['couteau', 'ciseaux'],
      ['cuillère', 'fourchette'],
      ['assiette', 'bol'],
      ['verre', 'tasse'],
      ['crayon', 'stylo'],
      ['stylo', 'feuille'],
      ['livre', 'journal'],
      ['cahier', 'journal intime'],
      ['roman', 'poème'],
      ['dictionnaire', 'encyclopédie'],
      ['chaise', 'banc'],
      ['lit', 'canapé'],
      ['armoire', 'vitrine'],
      ['fenêtre', 'porte'],
      ['escalier', 'ascenseur'],
      ['ascenseur', 'escalator'],
      ['lampe', 'bougie'],
      ['bougie', 'lanterne'],
      ['lampe de poche', 'lanterne'],
      ['miroir', 'vitre'],
      ['parapluie', 'ombrelle'],
      ['sac à dos', 'valise'],
      ['montre', 'bague'],
      ['montre-bracelet', 'réveil'],
      ['horloge', 'montre'],
      ['horloge', 'cadran solaire'],
      ['collier', 'bracelet'],
      ['bague', 'boucle d’oreille'],
      ['couronne', 'chapeau'],
      ['casque', 'chapeau'],
      ['costume', 'cravate'],
      ['écharpe', 'cravate'],
      ['veste', 'manteau'],
      ['chemise', 'pull'],
      ['jean', 'velours côtelé'],
      ['baskets', 'sandales'],
      ['soutien-gorge', 'bikini'],
    ],
    [L.ES]: [
      ['teléfono', 'tableta'],
      ['ordenador', 'portátil'],
      ['ratón', 'teclado'],
      ['televisión', 'radio'],
      ['auriculares', 'cascos'],
      ['micrófono', 'altavoz'],
      ['horno', 'microondas'],
      ['sartén', 'cazuela'],
      ['plato', 'bol'],
      ['cuchillo', 'tenedor'],
      ['cuchara', 'cucharón'],
      ['taza', 'jarra'],
      ['vaso', 'copa'],
      ['libro', 'revista'],
      ['bolígrafo', 'lápiz'],
      ['tijeras', 'cúter'],
      ['silla', 'taburete'],
      ['mesa', 'escritorio'],
      ['sofá', 'sillón'],
      ['cama', 'colchón'],
      ['armario', 'cómoda'],
      ['lámpara', 'vela'],
      ['paraguas', 'sombrilla'],
      ['bolso', 'mochila'],
      ['maleta', 'maletín'],
      ['cartera', 'monedero'],
      ['llave', 'candado'],
      ['reloj', 'pulsera'],
      ['gafas', 'lupa'],
      ['espejo', 'marco'],
      ['escoba', 'fregona'],
      ['aspiradora', 'barredora'],
      ['lavadora', 'lavavajillas'],
      ['nevera', 'congelador'],
      ['tostadora', 'hervidor'],
      ['cafetera', 'tetera'],
      ['batidora', 'licuadora'],
      ['ventilador', 'aire acondicionado'],
      ['calefactor', 'radiador'],
      ['mando', 'control'],
      ['cámara', 'videocámara'],
      ['impresora', 'escáner'],
      ['altavoz', 'amplificador'],
      ['cable', 'cargador'],
      ['enchufe', 'interruptor'],
      ['bombilla', 'neón'],
      ['clavo', 'tornillo'],
      ['martillo', 'destornillador'],
      ['taladro', 'sierra'],
      ['pala', 'pico'],
    ],
    [L.DE]: [
      ['Handy', 'Tablet'],
      ['Computer', 'Laptop'],
      ['Maus', 'Tastatur'],
      ['Fernseher', 'Radio'],
      ['Kopfhörer', 'Ohrstöpsel'],
      ['Mikrofon', 'Lautsprecher'],
      ['Ofen', 'Mikrowelle'],
      ['Pfanne', 'Topf'],
      ['Teller', 'Schüssel'],
      ['Messer', 'Gabel'],
      ['Löffel', 'Schöpfkelle'],
      ['Tasse', 'Becher'],
      ['Glas', 'Krug'],
      ['Buch', 'Zeitschrift'],
      ['Kugelschreiber', 'Bleistift'],
      ['Schere', 'Cutter'],
      ['Stuhl', 'Hocker'],
      ['Tisch', 'Schreibtisch'],
      ['Sofa', 'Sessel'],
      ['Bett', 'Matratze'],
      ['Schrank', 'Kommode'],
      ['Lampe', 'Kerze'],
      ['Regenschirm', 'Sonnenschirm'],
      ['Tasche', 'Rucksack'],
      ['Koffer', 'Aktenkoffer'],
      ['Geldbörse', 'Portemonnaie'],
      ['Schlüssel', 'Vorhängeschloss'],
      ['Uhr', 'Armband'],
      ['Brille', 'Lupe'],
      ['Spiegel', 'Rahmen'],
      ['Besen', 'Wischmopp'],
      ['Staubsauger', 'Kehrmaschine'],
      ['Waschmaschine', 'Spülmaschine'],
      ['Kühlschrank', 'Gefrierschrank'],
      ['Toaster', 'Wasserkocher'],
      ['Kaffeemaschine', 'Teekanne'],
      ['Mixer', 'Pürierstab'],
      ['Ventilator', 'Klimaanlage'],
      ['Heizung', 'Heizkörper'],
      ['Fernbedienung', 'Controller'],
      ['Kamera', 'Camcorder'],
      ['Drucker', 'Scanner'],
      ['Boxen', 'Verstärker'],
      ['Kabel', 'Ladegerät'],
      ['Steckdose', 'Schalter'],
      ['Glühbirne', 'Neonröhre'],
      ['Nagel', 'Schraube'],
      ['Hammer', 'Schraubenzieher'],
      ['Bohrer', 'Säge'],
      ['Schaufel', 'Spitzhacke'],
    ],
    [L.PT]: [
      ['telemóvel', 'tablet'],
      ['computador', 'portátil'],
      ['rato', 'teclado'],
      ['televisão', 'rádio'],
      ['auscultadores', 'auriculares'],
      ['microfone', 'coluna'],
      ['forno', 'micro-ondas'],
      ['frigideira', 'panela'],
      ['prato', 'tigela'],
      ['faca', 'garfo'],
      ['colher', 'concha'],
      ['chávena', 'caneca'],
      ['copo', 'taça'],
      ['livro', 'revista'],
      ['caneta', 'lápis'],
      ['tesoura', 'x-ato'],
      ['cadeira', 'banco'],
      ['mesa', 'secretária'],
      ['sofá', 'poltrona'],
      ['cama', 'colchão'],
      ['roupeiro', 'cómoda'],
      ['candeeiro', 'vela'],
      ['guarda-chuva', 'chapéu de sol'],
      ['saco', 'mochila'],
      ['mala', 'pasta'],
      ['carteira', 'porta-moedas'],
      ['chave', 'cadeado'],
      ['relógio', 'pulseira'],
      ['óculos', 'lupa'],
      ['espelho', 'moldura'],
      ['vassoura', 'esfregona'],
      ['aspirador', 'varredora'],
      ['máquina de lavar', 'máquina de lavar loiça'],
      ['frigorífico', 'congelador'],
      ['torradeira', 'chaleira'],
      ['cafeteira', 'bule'],
      ['liquidificador', 'batedeira'],
      ['ventoinha', 'ar condicionado'],
      ['aquecedor', 'radiador'],
      ['comando', 'controlo'],
      ['câmara', 'videocâmara'],
      ['impressora', 'scanner'],
      ['altifalante', 'amplificador'],
      ['cabo', 'carregador'],
      ['tomada', 'interruptor'],
      ['lâmpada', 'néon'],
      ['prego', 'parafuso'],
      ['martelo', 'chave de fendas'],
      ['berbequim', 'serra'],
      ['pá', 'picareta'],
    ],
  },
  [Category.Geography]: {
    [L.EN]: [
      ['Paris', 'Lyon'],
      ['London', 'Edinburgh'],
      ['New York', 'Los Angeles'],
      ['Tokyo', 'Kyoto'],
      ['Madrid', 'Barcelona'],
      ['Berlin', 'Munich'],
      ['Rome', 'Milan'],
      ['Brussels', 'Amsterdam'],
      ['Sydney', 'Melbourne'],
      ['Rio', 'São Paulo'],
      ['Toronto', 'Vancouver'],
      ['Chicago', 'Boston'],
      ['Miami', 'San Diego'],
      ['France', 'Germany'],
      ['Italy', 'Spain'],
      ['Japan', 'China'],
      ['Canada', 'USA'],
      ['Brazil', 'Argentina'],
      ['Egypt', 'Morocco'],
      ['Switzerland', 'Austria'],
      ['Mediterranean', 'Atlantic'],
      ['Sahara', 'Gobi'],
      ['Alps', 'Pyrenees'],
      ['Mississippi', 'Amazon'],
      ['Rhine', 'Danube'],
      ['Provence', 'Tuscany'],
      ['Normandy', 'Brittany'],
      ['Corsica', 'Sardinia'],
      ['Sicily', 'Crete'],
      ['Andorra', 'Monaco'],
      ['Cannes', 'Saint-Tropez'],
      ['Vienna', 'Prague'],
      ['Naples', 'Florence'],
      ['Venice', 'Verona'],
      ['Athens', 'Istanbul'],
      ['Cairo', 'Casablanca'],
      ['Beijing', 'Shanghai'],
      ['Seoul', 'Hanoi'],
      ['Bangkok', 'Singapore'],
      ['Dubai', 'Doha'],
      ['Mexico City', 'Havana'],
      ['Buenos Aires', 'Santiago'],
      ['Cape Verde', 'Madagascar'],
      ['Iceland', 'Greenland'],
      ['Hawaii', 'Tahiti'],
      ['Texas', 'Florida'],
      ['California', 'Nevada'],
      ['Maine', 'Vermont'],
      ['Niagara', 'Iguazu'],
      ['Everest', 'K2'],
    ],
    [L.FR]: [
      ['Paris', 'Londres'],
      ['New York', 'Los Angeles'],
      ['Rome', 'Athènes'],
      ['Sydney', 'Melbourne'],
      ['Rio de Janeiro', 'Buenos Aires'],
      ['Mont Everest', 'Népal'],
      ['Amazone', 'Nil'],
      ['Grand Canyon', 'Chutes du Niagara'],
      ['Taj Mahal', 'Grande Muraille de Chine'],
      ['Tour Eiffel', 'Statue de la Liberté'],
      ['Big Ben', 'Tour de Pise'],
      ['France', 'Tour Eiffel'],
      ['Allemagne', 'France'],
      ['Allemagne', 'bière'],
      ['Japon', 'Chine'],
      ['plage', 'montagne'],
      ['volcan', 'montagne'],
      ['désert', 'sable'],
      ['désert', 'savane'],
      ['jungle', 'forêt'],
      ['forêt', 'parc'],
      ['pyramide', 'temple'],
      ['château', 'maison'],
      ['château', 'palais'],
      ['bibliothèque', 'université'],
      ['musée', 'galerie'],
      ['boulangerie', 'boucherie'],
      ['boulangerie', 'pâtisserie'],
      ['cinéma', 'théâtre'],
      ['cinéma', 'bowling'],
      ['théâtre', 'opéra'],
      ['piscine', 'plage'],
      ['piscine', 'jacuzzi'],
      ['jacuzzi', 'sauna'],
      ['tente', 'cabane'],
      ['camping', 'tente'],
      ['pont', 'tunnel'],
      ['aéroport', 'gare'],
      ['hôpital', 'clinique'],
      ['restaurant', 'café'],
      ['supermarché', 'marché'],
      ['parc', 'jardin'],
      ['Paris', 'Lyon'],
      ['Marseille', 'Nice'],
      ['Bordeaux', 'Toulouse'],
      ['Strasbourg', 'Lille'],
      ['Madrid', 'Barcelone'],
      ['Berlin', 'Munich'],
      ['Rome', 'Milan'],
      ['Tokyo', 'Kyoto'],
    ],
    [L.ES]: [
      ['Madrid', 'Barcelona'],
      ['París', 'Lyon'],
      ['Londres', 'Edimburgo'],
      ['Nueva York', 'Los Ángeles'],
      ['Tokio', 'Kioto'],
      ['Berlín', 'Múnich'],
      ['Roma', 'Milán'],
      ['Bruselas', 'Ámsterdam'],
      ['Sevilla', 'Granada'],
      ['Valencia', 'Zaragoza'],
      ['Bilbao', 'San Sebastián'],
      ['Sídney', 'Melbourne'],
      ['Río', 'São Paulo'],
      ['España', 'Portugal'],
      ['Italia', 'Francia'],
      ['Japón', 'China'],
      ['Canadá', 'Estados Unidos'],
      ['Brasil', 'Argentina'],
      ['Egipto', 'Marruecos'],
      ['Suiza', 'Austria'],
      ['Mediterráneo', 'Atlántico'],
      ['Sahara', 'Gobi'],
      ['Alpes', 'Pirineos'],
      ['Ebro', 'Tajo'],
      ['Duero', 'Guadalquivir'],
      ['Andalucía', 'Cataluña'],
      ['Galicia', 'Asturias'],
      ['Mallorca', 'Menorca'],
      ['Sicilia', 'Cerdeña'],
      ['Andorra', 'Mónaco'],
      ['Cannes', 'Saint-Tropez'],
      ['Viena', 'Praga'],
      ['Nápoles', 'Florencia'],
      ['Venecia', 'Verona'],
      ['Atenas', 'Estambul'],
      ['El Cairo', 'Casablanca'],
      ['Pekín', 'Shanghái'],
      ['Seúl', 'Hanói'],
      ['Bangkok', 'Singapur'],
      ['Dubái', 'Doha'],
      ['Ciudad de México', 'La Habana'],
      ['Buenos Aires', 'Santiago'],
      ['Cabo Verde', 'Madagascar'],
      ['Islandia', 'Groenlandia'],
      ['Hawái', 'Tahití'],
      ['Tenerife', 'Gran Canaria'],
      ['Ibiza', 'Formentera'],
      ['Salamanca', 'Toledo'],
      ['Iguazú', 'Niágara'],
      ['Everest', 'K2'],
    ],
    [L.DE]: [
      ['Berlin', 'München'],
      ['Hamburg', 'Köln'],
      ['Frankfurt', 'Stuttgart'],
      ['Paris', 'Lyon'],
      ['London', 'Edinburgh'],
      ['New York', 'Los Angeles'],
      ['Tokio', 'Kyoto'],
      ['Madrid', 'Barcelona'],
      ['Rom', 'Mailand'],
      ['Brüssel', 'Amsterdam'],
      ['Wien', 'Salzburg'],
      ['Zürich', 'Genf'],
      ['Sydney', 'Melbourne'],
      ['Rio', 'São Paulo'],
      ['Deutschland', 'Österreich'],
      ['Italien', 'Spanien'],
      ['Japan', 'China'],
      ['Kanada', 'USA'],
      ['Brasilien', 'Argentinien'],
      ['Ägypten', 'Marokko'],
      ['Schweiz', 'Liechtenstein'],
      ['Mittelmeer', 'Atlantik'],
      ['Sahara', 'Gobi'],
      ['Alpen', 'Pyrenäen'],
      ['Rhein', 'Donau'],
      ['Elbe', 'Oder'],
      ['Bayern', 'Sachsen'],
      ['Korsika', 'Sardinien'],
      ['Sizilien', 'Kreta'],
      ['Andorra', 'Monaco'],
      ['Düsseldorf', 'Essen'],
      ['Bremen', 'Hannover'],
      ['Leipzig', 'Dresden'],
      ['Cannes', 'Saint-Tropez'],
      ['Wien', 'Prag'],
      ['Neapel', 'Florenz'],
      ['Venedig', 'Verona'],
      ['Athen', 'Istanbul'],
      ['Kairo', 'Casablanca'],
      ['Peking', 'Shanghai'],
      ['Seoul', 'Hanoi'],
      ['Bangkok', 'Singapur'],
      ['Dubai', 'Doha'],
      ['Mexiko-Stadt', 'Havanna'],
      ['Buenos Aires', 'Santiago'],
      ['Kap Verde', 'Madagaskar'],
      ['Island', 'Grönland'],
      ['Hawaii', 'Tahiti'],
      ['Niagara', 'Iguazú'],
      ['Everest', 'K2'],
    ],
    [L.PT]: [
      ['Lisboa', 'Porto'],
      ['Coimbra', 'Braga'],
      ['Faro', 'Évora'],
      ['Madeira', 'Açores'],
      ['Paris', 'Lyon'],
      ['Londres', 'Edimburgo'],
      ['Madrid', 'Barcelona'],
      ['Roma', 'Milão'],
      ['Berlim', 'Munique'],
      ['Bruxelas', 'Amesterdão'],
      ['Tóquio', 'Quioto'],
      ['Rio', 'São Paulo'],
      ['Salvador', 'Recife'],
      ['Brasília', 'Belo Horizonte'],
      ['Portugal', 'Espanha'],
      ['França', 'Alemanha'],
      ['Itália', 'Grécia'],
      ['Japão', 'China'],
      ['Canadá', 'Estados Unidos'],
      ['Brasil', 'Argentina'],
      ['Egito', 'Marrocos'],
      ['Mediterrâneo', 'Atlântico'],
      ['Sara', 'Gobi'],
      ['Alpes', 'Pirenéus'],
      ['Tejo', 'Douro'],
      ['Algarve', 'Alentejo'],
      ['Minho', 'Trás-os-Montes'],
      ['Córsega', 'Sardenha'],
      ['Sicília', 'Creta'],
      ['Andorra', 'Mónaco'],
      ['Cascais', 'Estoril'],
      ['Sintra', 'Mafra'],
      ['Aveiro', 'Viseu'],
      ['Setúbal', 'Beja'],
      ['Cannes', 'Saint-Tropez'],
      ['Viena', 'Praga'],
      ['Nápoles', 'Florença'],
      ['Veneza', 'Verona'],
      ['Atenas', 'Istambul'],
      ['Cairo', 'Casablanca'],
      ['Pequim', 'Xangai'],
      ['Seul', 'Hanói'],
      ['Banguecoque', 'Singapura'],
      ['Dubai', 'Doha'],
      ['Cidade do México', 'Havana'],
      ['Buenos Aires', 'Santiago'],
      ['Cabo Verde', 'Madagáscar'],
      ['Islândia', 'Gronelândia'],
      ['Havai', 'Taiti'],
      ['Everest', 'K2'],
    ],
  },
  [Category.Nature]: {
    [L.EN]: [
      ['beach', 'sea'],
      ['sea', 'ocean'],
      ['river', 'stream'],
      ['brook', 'creek'],
      ['lake', 'pond'],
      ['waterfall', 'cascade'],
      ['forest', 'jungle'],
      ['woods', 'grove'],
      ['mountain', 'hill'],
      ['volcano', 'crater'],
      ['desert', 'dune'],
      ['canyon', 'cliff'],
      ['island', 'peninsula'],
      ['reef', 'sandbar'],
      ['cloud', 'fog'],
      ['rain', 'snow'],
      ['storm', 'hurricane'],
      ['tornado', 'typhoon'],
      ['sun', 'moon'],
      ['star', 'planet'],
      ['comet', 'asteroid'],
      ['tree', 'shrub'],
      ['oak', 'pine'],
      ['rose', 'tulip'],
      ['poppy', 'daisy'],
      ['moss', 'lichen'],
      ['ivy', 'vine'],
      ['leaf', 'petal'],
      ['rock', 'pebble'],
      ['rainbow', 'aurora'],
      ['iceberg', 'glacier'],
      ['swamp', 'marsh'],
      ['mangrove', 'lagoon'],
      ['delta', 'estuary'],
      ['cavern', 'tunnel'],
      ['dune', 'oasis'],
      ['taiga', 'tundra'],
      ['prairie', 'steppe'],
      ['savanna', 'pampas'],
      ['maple', 'birch'],
      ['willow', 'poplar'],
      ['cedar', 'cypress'],
      ['lavender', 'sage'],
      ['sunflower', 'dandelion'],
      ['lily', 'iris'],
      ['fern', 'cactus'],
      ['mushroom', 'mold'],
      ['coral', 'sponge'],
      ['crystal', 'quartz'],
      ['lava', 'magma'],
    ],
    [L.FR]: [
      ['soleil', 'lune'],
      ['lune', 'soleil'],
      ['lune', 'planète'],
      ['planète', 'étoile'],
      ['comète', 'astéroïde'],
      ['météore', 'satellite'],
      ['tremblement de terre', 'tsunami'],
      ['nuage', 'brouillard'],
      ['neige', 'grêle'],
      ['pluie', 'averse'],
      ['vent', 'bourrasque'],
      ['orage', 'ouragan'],
      ['ouragan', 'tornade'],
      ['ciel', 'nuages'],
      ['volcan', 'cratère'],
      ['rivière', 'lac'],
      ['océan', 'rivière'],
      ['fontaine', 'cascade'],
      ['lac', 'étang'],
      ['mer', 'océan'],
      ['plage', 'mer'],
      ['ruisseau', 'torrent'],
      ['cascade', 'chute d’eau'],
      ['bois', 'clairière'],
      ['montagne', 'colline'],
      ['canyon', 'falaise'],
      ['île', 'péninsule'],
      ['récif', 'banc de sable'],
      ['arbre', 'arbuste'],
      ['bouleau', 'arbuste'],
      ['chêne', 'sapin'],
      ['érable', 'bouleau'],
      ['saule', 'peuplier'],
      ['cèdre', 'cyprès'],
      ['rose', 'tulipe'],
      ['coquelicot', 'marguerite'],
      ['lavande', 'sauge'],
      ['lys', 'iris'],
      ['mousse', 'lichen'],
      ['lierre', 'vigne'],
      ['fougère', 'cactus'],
      ['feuille', 'pétale'],
      ['rocher', 'caillou'],
      ['arc-en-ciel', 'aurore'],
      ['iceberg', 'banquise'],
      ['marais', 'marécage'],
      ['gouffre', 'grotte'],
      ['caverne', 'tunnel'],
      ['dune', 'oasis'],
      ['taïga', 'toundra'],
    ],
    [L.ES]: [
      ['playa', 'mar'],
      ['mar', 'océano'],
      ['río', 'arroyo'],
      ['cascada', 'catarata'],
      ['lago', 'estanque'],
      ['fuente', 'manantial'],
      ['bosque', 'selva'],
      ['arboleda', 'claro'],
      ['montaña', 'colina'],
      ['volcán', 'cráter'],
      ['desierto', 'duna'],
      ['cañón', 'acantilado'],
      ['isla', 'península'],
      ['arrecife', 'banco de arena'],
      ['nube', 'niebla'],
      ['lluvia', 'nieve'],
      ['tormenta', 'huracán'],
      ['tornado', 'tifón'],
      ['sol', 'luna'],
      ['estrella', 'planeta'],
      ['cometa', 'asteroide'],
      ['árbol', 'arbusto'],
      ['roble', 'pino'],
      ['rosa', 'tulipán'],
      ['amapola', 'margarita'],
      ['musgo', 'liquen'],
      ['hiedra', 'vid'],
      ['hoja', 'pétalo'],
      ['roca', 'guijarro'],
      ['arcoíris', 'aurora'],
      ['iceberg', 'glaciar'],
      ['pantano', 'marisma'],
      ['manglar', 'laguna'],
      ['delta', 'estuario'],
      ['cueva', 'túnel'],
      ['duna', 'oasis'],
      ['taiga', 'tundra'],
      ['pradera', 'estepa'],
      ['sabana', 'pampa'],
      ['arce', 'abedul'],
      ['sauce', 'álamo'],
      ['cedro', 'ciprés'],
      ['lavanda', 'salvia'],
      ['girasol', 'diente de león'],
      ['lirio', 'iris'],
      ['helecho', 'cactus'],
      ['seta', 'moho'],
      ['coral', 'esponja'],
      ['cristal', 'cuarzo'],
      ['lava', 'magma'],
    ],
    [L.DE]: [
      ['Strand', 'Meer'],
      ['Meer', 'Ozean'],
      ['Fluss', 'Bach'],
      ['Wasserfall', 'Kaskade'],
      ['See', 'Teich'],
      ['Quelle', 'Brunnen'],
      ['Wald', 'Dschungel'],
      ['Hain', 'Lichtung'],
      ['Berg', 'Hügel'],
      ['Vulkan', 'Krater'],
      ['Wüste', 'Düne'],
      ['Schlucht', 'Klippe'],
      ['Insel', 'Halbinsel'],
      ['Riff', 'Sandbank'],
      ['Wolke', 'Nebel'],
      ['Regen', 'Schnee'],
      ['Sturm', 'Hurrikan'],
      ['Tornado', 'Taifun'],
      ['Sonne', 'Mond'],
      ['Stern', 'Planet'],
      ['Komet', 'Asteroid'],
      ['Baum', 'Strauch'],
      ['Eiche', 'Kiefer'],
      ['Rose', 'Tulpe'],
      ['Mohnblume', 'Gänseblümchen'],
      ['Moos', 'Flechte'],
      ['Efeu', 'Weinrebe'],
      ['Blatt', 'Blütenblatt'],
      ['Felsen', 'Kieselstein'],
      ['Regenbogen', 'Polarlicht'],
      ['Eisberg', 'Gletscher'],
      ['Sumpf', 'Moor'],
      ['Mangrove', 'Lagune'],
      ['Delta', 'Mündung'],
      ['Höhle', 'Tunnel'],
      ['Düne', 'Oase'],
      ['Taiga', 'Tundra'],
      ['Wiese', 'Steppe'],
      ['Savanne', 'Pampa'],
      ['Ahorn', 'Birke'],
      ['Weide', 'Pappel'],
      ['Zeder', 'Zypresse'],
      ['Lavendel', 'Salbei'],
      ['Sonnenblume', 'Löwenzahn'],
      ['Lilie', 'Iris'],
      ['Farn', 'Kaktus'],
      ['Pilz', 'Schimmel'],
      ['Koralle', 'Schwamm'],
      ['Kristall', 'Quarz'],
      ['Lava', 'Magma'],
    ],
    [L.PT]: [
      ['praia', 'mar'],
      ['mar', 'oceano'],
      ['rio', 'ribeiro'],
      ['cascata', 'catarata'],
      ['lago', 'lagoa'],
      ['fonte', 'nascente'],
      ['floresta', 'selva'],
      ['bosque', 'clareira'],
      ['montanha', 'colina'],
      ['vulcão', 'cratera'],
      ['deserto', 'duna'],
      ['cânion', 'falésia'],
      ['ilha', 'península'],
      ['recife', 'banco de areia'],
      ['nuvem', 'nevoeiro'],
      ['chuva', 'neve'],
      ['tempestade', 'furacão'],
      ['tornado', 'tufão'],
      ['sol', 'lua'],
      ['estrela', 'planeta'],
      ['cometa', 'asteroide'],
      ['árvore', 'arbusto'],
      ['carvalho', 'pinheiro'],
      ['rosa', 'tulipa'],
      ['papoila', 'margarida'],
      ['musgo', 'líquen'],
      ['hera', 'videira'],
      ['folha', 'pétala'],
      ['rocha', 'seixo'],
      ['arco-íris', 'aurora'],
      ['icebergue', 'glaciar'],
      ['pântano', 'charco'],
      ['mangal', 'albufeira'],
      ['delta', 'estuário'],
      ['caverna', 'túnel'],
      ['duna', 'oásis'],
      ['taiga', 'tundra'],
      ['pradaria', 'estepe'],
      ['savana', 'pampa'],
      ['ácer', 'bétula'],
      ['salgueiro', 'álamo'],
      ['cedro', 'cipreste'],
      ['alfazema', 'sálvia'],
      ['girassol', 'dente-de-leão'],
      ['lírio', 'íris'],
      ['feto', 'cato'],
      ['cogumelo', 'bolor'],
      ['coral', 'esponja'],
      ['cristal', 'quartzo'],
      ['lava', 'magma'],
    ],
  },
  [Category.Sports]: {
    [L.EN]: [
      ['football', 'rugby'],
      ['soccer', 'handball'],
      ['basketball', 'handball'],
      ['tennis', 'badminton'],
      ['table tennis', 'badminton'],
      ['boxing', 'wrestling'],
      ['judo', 'karate'],
      ['skiing', 'snowboarding'],
      ['skating', 'hockey'],
      ['surfing', 'windsurfing'],
      ['sailing', 'rowing'],
      ['kayak', 'canoe'],
      ['swimming', 'diving'],
      ['running', 'marathon'],
      ['sprint', 'hurdles'],
      ['long jump', 'high jump'],
      ['javelin', 'shot put'],
      ['cycling', 'mountain biking'],
      ['motorcycle', 'scooter'],
      ['F1', 'rally'],
      ['golf', 'minigolf'],
      ['bowling', 'curling'],
      ['darts', 'pool'],
      ['fencing', 'archery'],
      ['yoga', 'pilates'],
      ['climbing', 'hiking'],
      ['parkour', 'breakdance'],
      ['NBA', 'NFL'],
      ['Wimbledon', 'US Open'],
      ['Tour de France', 'Giro'],
      ['ping-pong', 'squash'],
      ['squash', 'racquetball'],
      ['baseball', 'cricket'],
      ['Muay Thai', 'MMA'],
      ['krav maga', 'aikido'],
      ['taekwondo', 'kung fu'],
      ['gymnastics', 'acrobatics'],
      ['trampoline', 'uneven bars'],
      ['figure skating', 'speed skating'],
      ['luge', 'bobsled'],
      ['biathlon', 'triathlon'],
      ['curling', 'shuffleboard'],
      ['trap shooting', 'sport shooting'],
      ['equestrian', 'dressage'],
      ['polo', 'horseball'],
      ['cricket', 'kabaddi'],
      ['NHL', 'MLS'],
      ['MLB', 'Premier League'],
      ['UFC', 'WWE'],
      ['World Cup', 'Euro'],
    ],
    [L.FR]: [
      ['football', 'rugby'],
      ['football', 'handball'],
      ['basket', 'handball'],
      ['tennis', 'badminton'],
      ['tennis de table', 'badminton'],
      ['boxe', 'lutte'],
      ['judo', 'karaté'],
      ['ski', 'snowboard'],
      ['patinage', 'hockey'],
      ['surf', 'planche à voile'],
      ['voile', 'aviron'],
      ['kayak', 'canoë'],
      ['natation', 'plongée'],
      ['course', 'marathon'],
      ['sprint', 'haies'],
      ['saut en longueur', 'saut en hauteur'],
      ['lancer du javelot', 'lancer du poids'],
      ['cyclisme', 'VTT'],
      ['moto', 'scooter'],
      ['F1', 'rallye'],
      ['golf', 'minigolf'],
      ['bowling', 'pétanque'],
      ['fléchettes', 'billard'],
      ['escrime', 'tir à l’arc'],
      ['yoga', 'pilates'],
      ['escalade', 'randonnée'],
      ['parkour', 'breakdance'],
      ['PSG', 'OM'],
      ['Roland-Garros', 'Wimbledon'],
      ['Tour de France', 'Giro'],
      ['ping-pong', 'squash'],
      ['squash', 'racquetball'],
      ['base-ball', 'cricket'],
      ['boxe thaï', 'MMA'],
      ['krav maga', 'aïkido'],
      ['taekwondo', 'kung-fu'],
      ['gymnastique', 'acrobatie'],
      ['trampoline', 'barres asymétriques'],
      ['patinage artistique', 'patinage de vitesse'],
      ['luge', 'bobsleigh'],
      ['biathlon', 'triathlon'],
      ['curling', 'shuffleboard'],
      ['tir au pigeon', 'tir sportif'],
      ['équitation', 'dressage'],
      ['polo', 'horse-ball'],
      ['cricket', 'kabaddi'],
      ['NBA', 'NHL'],
      ['NFL', 'MLB'],
      ['UFC', 'WWE'],
      ['Coupe du monde', 'Euro'],
    ],
    [L.ES]: [
      ['fútbol', 'rugby'],
      ['fútbol', 'fútbol sala'],
      ['baloncesto', 'balonmano'],
      ['tenis', 'bádminton'],
      ['tenis de mesa', 'bádminton'],
      ['boxeo', 'lucha'],
      ['judo', 'kárate'],
      ['esquí', 'snowboard'],
      ['patinaje', 'hockey'],
      ['surf', 'windsurf'],
      ['vela', 'remo'],
      ['kayak', 'canoa'],
      ['natación', 'buceo'],
      ['correr', 'maratón'],
      ['velocidad', 'vallas'],
      ['salto de longitud', 'salto de altura'],
      ['jabalina', 'lanzamiento de peso'],
      ['ciclismo', 'mountain bike'],
      ['moto', 'scooter'],
      ['F1', 'rally'],
      ['golf', 'minigolf'],
      ['bolos', 'petanca'],
      ['dardos', 'billar'],
      ['esgrima', 'tiro con arco'],
      ['yoga', 'pilates'],
      ['escalada', 'senderismo'],
      ['parkour', 'breakdance'],
      ['Real Madrid', 'Barça'],
      ['Roland Garros', 'Wimbledon'],
      ['Vuelta', 'Giro'],
      ['ping-pong', 'squash'],
      ['squash', 'racquetball'],
      ['béisbol', 'cricket'],
      ['muay thai', 'MMA'],
      ['krav maga', 'aikido'],
      ['taekwondo', 'kung fu'],
      ['gimnasia', 'acrobacia'],
      ['trampolín', 'barras asimétricas'],
      ['patinaje artístico', 'patinaje de velocidad'],
      ['luge', 'bobsleigh'],
      ['biatlón', 'triatlón'],
      ['curling', 'shuffleboard'],
      ['tiro al plato', 'tiro deportivo'],
      ['equitación', 'doma'],
      ['polo', 'horseball'],
      ['cricket', 'kabaddi'],
      ['NBA', 'NHL'],
      ['NFL', 'MLB'],
      ['UFC', 'WWE'],
      ['Mundial', 'Eurocopa'],
    ],
    [L.DE]: [
      ['Fußball', 'Rugby'],
      ['Fußball', 'Hallenfußball'],
      ['Basketball', 'Handball'],
      ['Tennis', 'Badminton'],
      ['Tischtennis', 'Badminton'],
      ['Boxen', 'Ringen'],
      ['Judo', 'Karate'],
      ['Skifahren', 'Snowboarden'],
      ['Eislaufen', 'Eishockey'],
      ['Surfen', 'Windsurfen'],
      ['Segeln', 'Rudern'],
      ['Kajak', 'Kanu'],
      ['Schwimmen', 'Tauchen'],
      ['Laufen', 'Marathon'],
      ['Sprint', 'Hürdenlauf'],
      ['Weitsprung', 'Hochsprung'],
      ['Speerwurf', 'Kugelstoßen'],
      ['Radsport', 'Mountainbike'],
      ['Motorrad', 'Roller'],
      ['F1', 'Rallye'],
      ['Golf', 'Minigolf'],
      ['Bowling', 'Kegeln'],
      ['Darts', 'Billard'],
      ['Fechten', 'Bogenschießen'],
      ['Yoga', 'Pilates'],
      ['Klettern', 'Wandern'],
      ['Parkour', 'Breakdance'],
      ['Bayern', 'Dortmund'],
      ['Wimbledon', 'US Open'],
      ['Tour de France', 'Giro'],
      ['Pingpong', 'Squash'],
      ['Squash', 'Racquetball'],
      ['Baseball', 'Cricket'],
      ['Muay Thai', 'MMA'],
      ['Krav Maga', 'Aikido'],
      ['Taekwondo', 'Kung Fu'],
      ['Turnen', 'Akrobatik'],
      ['Trampolin', 'Stufenbarren'],
      ['Eiskunstlauf', 'Eisschnelllauf'],
      ['Rodeln', 'Bob'],
      ['Biathlon', 'Triathlon'],
      ['Curling', 'Shuffleboard'],
      ['Trap', 'Sportschießen'],
      ['Reiten', 'Dressur'],
      ['Polo', 'Horseball'],
      ['Cricket', 'Kabaddi'],
      ['NHL', 'MLS'],
      ['MLB', 'Bundesliga'],
      ['UFC', 'WWE'],
      ['WM', 'EM'],
    ],
    [L.PT]: [
      ['futebol', 'râguebi'],
      ['futebol', 'futsal'],
      ['basquetebol', 'andebol'],
      ['ténis', 'badmínton'],
      ['ténis de mesa', 'badmínton'],
      ['boxe', 'luta livre'],
      ['judo', 'karaté'],
      ['esqui', 'snowboard'],
      ['patinagem', 'hóquei'],
      ['surf', 'windsurf'],
      ['vela', 'remo'],
      ['caiaque', 'canoa'],
      ['natação', 'mergulho'],
      ['corrida', 'maratona'],
      ['velocidade', 'barreiras'],
      ['salto em comprimento', 'salto em altura'],
      ['dardo', 'peso'],
      ['ciclismo', 'BTT'],
      ['mota', 'scooter'],
      ['F1', 'rali'],
      ['golfe', 'minigolfe'],
      ['bowling', 'petanca'],
      ['dardos', 'bilhar'],
      ['esgrima', 'tiro com arco'],
      ['yoga', 'pilates'],
      ['escalada', 'caminhada'],
      ['parkour', 'breakdance'],
      ['Benfica', 'Porto'],
      ['Wimbledon', 'Roland Garros'],
      ['Volta a Portugal', 'Tour de France'],
      ['ping-pong', 'squash'],
      ['squash', 'racquetball'],
      ['basebol', 'críquete'],
      ['muay thai', 'MMA'],
      ['krav maga', 'aikido'],
      ['taekwondo', 'kung fu'],
      ['ginástica', 'acrobacia'],
      ['trampolim', 'paralelas assimétricas'],
      ['patinagem artística', 'patinagem de velocidade'],
      ['luge', 'bobsleigh'],
      ['biatlo', 'triatlo'],
      ['curling', 'shuffleboard'],
      ['tiro ao prato', 'tiro desportivo'],
      ['equitação', 'dressage'],
      ['polo', 'horseball'],
      ['críquete', 'kabaddi'],
      ['NBA', 'NHL'],
      ['NFL', 'MLB'],
      ['UFC', 'WWE'],
      ['Mundial', 'Euro'],
    ],
  },
  [Category.Movies]: {
    [L.EN]: [
      ['Star Wars', 'Star Trek'],
      ['Batman', 'Superman'],
      ['Spider-Man', 'Iron Man'],
      ['Captain America', 'Thor'],
      ['Marvel', 'DC Comics'],
      ['Harry Potter', 'Lord of the Rings'],
      ['The Hobbit', 'Lord of the Rings'],
      ['Indiana Jones', 'Lara Croft'],
      ['Titanic', 'Pearl Harbor'],
      ['The Matrix', 'Inception'],
      ['Frozen', 'Tangled'],
      ['The Lion King', 'Madagascar'],
      ['Toy Story', 'Cars'],
      ['Pixar', 'DreamWorks'],
      ['Disney', 'Pixar'],
      ['Jurassic Park', 'King Kong'],
      ['Avengers', 'Justice League'],
      ['James Bond', 'Mission Impossible'],
      ['Sherlock', 'Poirot'],
      ['Rocky', 'Rambo'],
      ['Terminator', 'RoboCop'],
      ['Friends', 'How I Met Your Mother'],
      ['Breaking Bad', 'Better Call Saul'],
      ['Game of Thrones', 'Vikings'],
      ['Squid Game', 'Money Heist'],
      ['Stranger Things', 'Dark'],
      ['Netflix', 'Disney+'],
      ['PlayStation', 'Xbox'],
      ['Mario', 'Sonic'],
      ['SpongeBob', 'Patrick'],
      ['Pulp Fiction', 'Reservoir Dogs'],
      ['The Godfather', 'Goodfellas'],
      ['Forrest Gump', 'Big Fish'],
      ['Top Gun', 'Rocky'],
      ['Alien', 'Predator'],
      ['Aliens', 'Terminator'],
      ['Shrek', 'Kung Fu Panda'],
      ['Finding Nemo', 'Finding Dory'],
      ['Aladdin', 'Mulan'],
      ['Cinderella', 'Snow White'],
      ['Wizard of Oz', 'Alice in Wonderland'],
      ['Hunger Games', 'Divergent'],
      ['Twilight', 'Vampire Diaries'],
      ['La La Land', 'Whiplash'],
      ['Joker', 'Dark Knight'],
      ['Inception', 'Interstellar'],
      ['Avatar', 'Pocahontas'],
      ['Gladiator', 'Braveheart'],
      ['Saving Private Ryan', 'Dunkirk'],
      ['Pirates of the Caribbean', 'Goonies'],
    ],
    [L.FR]: [
      ['Star Wars', 'Star Trek'],
      ['Batman', 'Superman'],
      ['Spider-Man', 'Iron Man'],
      ['Captain America', 'Thor'],
      ['Marvel', 'DC Comics'],
      ['Harry Potter', 'Le Seigneur des Anneaux'],
      ['Le Hobbit', 'Le Seigneur des Anneaux'],
      ['Indiana Jones', 'Lara Croft'],
      ['Titanic', 'Pearl Harbor'],
      ['Matrix', 'Inception'],
      ['La Reine des Neiges', 'Raiponce'],
      ['Le Roi Lion', 'Madagascar'],
      ['Toy Story', 'Cars'],
      ['Pixar', 'DreamWorks'],
      ['Disney', 'Pixar'],
      ['Jurassic Park', 'King Kong'],
      ['Avengers', 'Justice League'],
      ['James Bond', 'OSS 117'],
      ['Astérix', 'Tintin'],
      ['Les Bronzés', 'La Grande Vadrouille'],
      ['Les Visiteurs', 'Kaamelott'],
      ['Friends', 'How I Met Your Mother'],
      ['Breaking Bad', 'Better Call Saul'],
      ['Game of Thrones', 'Vikings'],
      ['Squid Game', 'La Casa de Papel'],
      ['Stranger Things', 'Dark'],
      ['Netflix', 'Disney+'],
      ['PlayStation', 'Xbox'],
      ['Mario', 'Sonic'],
      ['Picsou', 'Donald'],
      ['Pulp Fiction', 'Reservoir Dogs'],
      ['Le Parrain', 'Les Affranchis'],
      ['Forrest Gump', 'Big Fish'],
      ['Top Gun', 'Rocky'],
      ['Alien', 'Predator'],
      ['Aliens', 'Terminator'],
      ['Shrek', 'Kung Fu Panda'],
      ['Le Monde de Nemo', 'Le Monde de Dory'],
      ['Aladdin', 'Mulan'],
      ['Cendrillon', 'Blanche-Neige'],
      ["Le Magicien d'Oz", 'Alice au pays des merveilles'],
      ['Hunger Games', 'Divergente'],
      ['Twilight', 'Vampire Diaries'],
      ['La La Land', 'Whiplash'],
      ['Joker', 'Le Chevalier Noir'],
      ['Le Grand Bleu', 'Léon'],
      ['Amélie Poulain', 'Le Fabuleux Destin'],
      ['Intouchables', 'Hors normes'],
      ['La Haine', 'Banlieue 13'],
      ['Bienvenue chez les Ch’tis', 'Rien à déclarer'],
    ],
    [L.ES]: [
      ['Star Wars', 'Star Trek'],
      ['Batman', 'Superman'],
      ['Spider-Man', 'Iron Man'],
      ['Capitán América', 'Thor'],
      ['Marvel', 'DC'],
      ['Harry Potter', 'El Señor de los Anillos'],
      ['El Hobbit', 'El Señor de los Anillos'],
      ['Indiana Jones', 'Lara Croft'],
      ['Titanic', 'Pearl Harbor'],
      ['Matrix', 'Origen'],
      ['Frozen', 'Enredados'],
      ['El Rey León', 'Madagascar'],
      ['Toy Story', 'Cars'],
      ['Pixar', 'DreamWorks'],
      ['Disney', 'Pixar'],
      ['Jurassic Park', 'King Kong'],
      ['Vengadores', 'Liga de la Justicia'],
      ['James Bond', 'Misión Imposible'],
      ['Sherlock', 'Poirot'],
      ['Rocky', 'Rambo'],
      ['Terminator', 'RoboCop'],
      ['Friends', 'Cómo conocí a vuestra madre'],
      ['Breaking Bad', 'Better Call Saul'],
      ['Juego de Tronos', 'Vikings'],
      ['El juego del calamar', 'La Casa de Papel'],
      ['Stranger Things', 'Dark'],
      ['Netflix', 'Disney+'],
      ['PlayStation', 'Xbox'],
      ['Mario', 'Sonic'],
      ['Bob Esponja', 'Patricio'],
      ['Pulp Fiction', 'Reservoir Dogs'],
      ['El Padrino', 'Uno de los nuestros'],
      ['Forrest Gump', 'Big Fish'],
      ['Top Gun', 'Rocky'],
      ['Alien', 'Depredador'],
      ['Aliens', 'Terminator'],
      ['Shrek', 'Kung Fu Panda'],
      ['Buscando a Nemo', 'Buscando a Dory'],
      ['Aladdín', 'Mulán'],
      ['Cenicienta', 'Blancanieves'],
      ['El mago de Oz', 'Alicia en el País de las Maravillas'],
      ['Los juegos del hambre', 'Divergente'],
      ['Crepúsculo', 'Vampire Diaries'],
      ['La La Land', 'Whiplash'],
      ['Joker', 'El caballero oscuro'],
      ['Origen', 'Interstellar'],
      ['Avatar', 'Pocahontas'],
      ['Gladiator', 'Braveheart'],
      ['Salvar al soldado Ryan', 'Dunkerque'],
      ['Piratas del Caribe', 'Los Goonies'],
    ],
    [L.DE]: [
      ['Star Wars', 'Star Trek'],
      ['Batman', 'Superman'],
      ['Spider-Man', 'Iron Man'],
      ['Captain America', 'Thor'],
      ['Marvel', 'DC'],
      ['Harry Potter', 'Der Herr der Ringe'],
      ['Der Hobbit', 'Der Herr der Ringe'],
      ['Indiana Jones', 'Lara Croft'],
      ['Titanic', 'Pearl Harbor'],
      ['Matrix', 'Inception'],
      ['Die Eiskönigin', 'Rapunzel'],
      ['Der König der Löwen', 'Madagascar'],
      ['Toy Story', 'Cars'],
      ['Pixar', 'DreamWorks'],
      ['Disney', 'Pixar'],
      ['Jurassic Park', 'King Kong'],
      ['Avengers', 'Justice League'],
      ['James Bond', 'Mission Impossible'],
      ['Sherlock', 'Poirot'],
      ['Rocky', 'Rambo'],
      ['Terminator', 'RoboCop'],
      ['Friends', 'How I Met Your Mother'],
      ['Breaking Bad', 'Better Call Saul'],
      ['Game of Thrones', 'Vikings'],
      ['Squid Game', 'Haus des Geldes'],
      ['Stranger Things', 'Dark'],
      ['Netflix', 'Disney+'],
      ['PlayStation', 'Xbox'],
      ['Mario', 'Sonic'],
      ['SpongeBob', 'Patrick'],
      ['Pulp Fiction', 'Reservoir Dogs'],
      ['Der Pate', 'Goodfellas'],
      ['Forrest Gump', 'Big Fish'],
      ['Top Gun', 'Rocky'],
      ['Alien', 'Predator'],
      ['Aliens', 'Terminator'],
      ['Shrek', 'Kung Fu Panda'],
      ['Findet Nemo', 'Findet Dorie'],
      ['Aladdin', 'Mulan'],
      ['Aschenputtel', 'Schneewittchen'],
      ['Der Zauberer von Oz', 'Alice im Wunderland'],
      ['Tribute von Panem', 'Die Bestimmung'],
      ['Twilight', 'Vampire Diaries'],
      ['La La Land', 'Whiplash'],
      ['Joker', 'The Dark Knight'],
      ['Inception', 'Interstellar'],
      ['Avatar', 'Pocahontas'],
      ['Gladiator', 'Braveheart'],
      ['Der Soldat James Ryan', 'Dunkirk'],
      ['Pirates of the Caribbean', 'Die Goonies'],
    ],
    [L.PT]: [
      ['Star Wars', 'Star Trek'],
      ['Batman', 'Super-Homem'],
      ['Homem-Aranha', 'Homem de Ferro'],
      ['Capitão América', 'Thor'],
      ['Marvel', 'DC'],
      ['Harry Potter', 'O Senhor dos Anéis'],
      ['O Hobbit', 'O Senhor dos Anéis'],
      ['Indiana Jones', 'Lara Croft'],
      ['Titanic', 'Pearl Harbor'],
      ['Matrix', 'A Origem'],
      ['Frozen', 'Entrelaçados'],
      ['O Rei Leão', 'Madagáscar'],
      ['Toy Story', 'Carros'],
      ['Pixar', 'DreamWorks'],
      ['Disney', 'Pixar'],
      ['Jurassic Park', 'King Kong'],
      ['Vingadores', 'Liga da Justiça'],
      ['James Bond', 'Missão Impossível'],
      ['Sherlock', 'Poirot'],
      ['Rocky', 'Rambo'],
      ['Exterminador', 'RoboCop'],
      ['Friends', 'Como Conheci a Vossa Mãe'],
      ['Breaking Bad', 'Better Call Saul'],
      ['Guerra dos Tronos', 'Vikings'],
      ['Squid Game', 'La Casa de Papel'],
      ['Stranger Things', 'Dark'],
      ['Netflix', 'Disney+'],
      ['PlayStation', 'Xbox'],
      ['Mario', 'Sonic'],
      ['SpongeBob', 'Patrick'],
      ['Pulp Fiction', 'Cães Danados'],
      ['O Padrinho', 'Tudo Bons Rapazes'],
      ['Forrest Gump', 'Big Fish'],
      ['Top Gun', 'Rocky'],
      ['Alien', 'Predador'],
      ['Aliens', 'Exterminador'],
      ['Shrek', 'Kung Fu Panda'],
      ['À Procura de Nemo', 'À Procura de Dory'],
      ['Aladino', 'Mulan'],
      ['Cinderela', 'Branca de Neve'],
      ['O Feiticeiro de Oz', 'Alice no País das Maravilhas'],
      ['Jogos da Fome', 'Divergente'],
      ['Crepúsculo', 'Vampire Diaries'],
      ['La La Land', 'Whiplash'],
      ['Joker', 'Cavaleiro das Trevas'],
      ['Origem', 'Interstellar'],
      ['Avatar', 'Pocahontas'],
      ['Gladiador', 'Braveheart'],
      ['Resgate Soldado Ryan', 'Dunkirk'],
      ['Piratas das Caraíbas', 'Os Goonies'],
    ],
  },
  [Category.Slang]: {
    [L.EN]: [
      ['dude', 'bro'],
      ['chick', 'lady'],
      ['buddy', 'pal'],
      ['cop', 'pig'],
      ['cash', 'dough'],
      ['dough', 'bucks'],
      ['bucks', 'bread'],
      ['ride', 'wheels'],
      ['crib', 'pad'],
      ['book', 'novel'],
      ['job', 'gig'],
      ['food', 'chow'],
      ['cig', 'smoke'],
      ['drink', 'sip'],
      ['drunk', 'hammered'],
      ['love', 'dig'],
      ['shady', 'sus'],
      ['crazy', 'nuts'],
      ['annoying', 'lame'],
      ['mad', 'pissed'],
      ['clean', 'sharp'],
      ['sick', 'dope'],
      ['slap', 'smack'],
      ['snooze', 'nap'],
      ['peep', 'glance'],
      ['work', 'grind'],
      ['chill', 'bum'],
      ['swipe', 'nick'],
      ['flirt', 'hit on'],
      ['weird', 'odd'],
      ['mom', 'momma'],
      ['dad', 'pops'],
      ['kid', 'brat'],
      ['youngster', 'tot'],
      ['thug', 'hood'],
      ['salty', 'bitter'],
      ['yo', 'wassup'],
      ['fam', 'homie'],
      ['booze', 'hooch'],
      ['party', 'rager'],
      ['club', 'spot'],
      ['busted', 'caught'],
      ['bored', 'fed up'],
      ['lousy', 'crap'],
      ['cheesy', 'corny'],
      ['clever', 'sharp'],
      ['show-off', 'poser'],
      ['yuppie', 'hipster'],
      ['nab', 'pinch'],
      ['trashed', 'wasted'],
    ],
    [L.FR]: [
      ['mec', 'gars'],
      ['meuf', 'nana'],
      ['pote', 'poto'],
      ['flic', 'keuf'],
      ['thune', 'blé'],
      ['blé', 'fric'],
      ['fric', 'oseille'],
      ['bagnole', 'caisse'],
      ['baraque', 'piaule'],
      ['bouquin', 'roman'],
      ['taf', 'boulot'],
      ['bouffe', 'casse-croûte'],
      ['clope', 'sèche'],
      ['picoler', 'siroter'],
      ['cuite', 'biture'],
      ['kiffer', 'adorer'],
      ['chelou', 'louche'],
      ['ouf', 'dingue'],
      ['relou', 'chiant'],
      ['vénère', 'énervé'],
      ['nickel', 'clean'],
      ['mortel', 'dément'],
      ['claque', 'mandale'],
      ['pioncer', 'roupiller'],
      ['mater', 'zieuter'],
      ['bosser', 'taffer'],
      ['glander', 'buller'],
      ['piquer', 'chouraver'],
      ['pécho', 'draguer'],
      ['zarbi', 'bizarre'],
      ['daron', 'vieux'],
      ['daronne', 'mère'],
      ['gosse', 'mioche'],
      ['môme', 'gamin'],
      ['caillera', 'racaille'],
      ['seum', 'dégoût'],
      ['wesh', 'salut'],
      ['frère', 'frérot'],
      ['tise', 'picole'],
      ['teuf', 'fête'],
      ['boîte', 'club'],
      ['cramé', 'grillé'],
      ['blasé', 'saoulé'],
      ['nase', 'nul'],
      ['ringard', 'has-been'],
      ['malin', 'futé'],
      ['kéké', 'frimeur'],
      ['bobo', 'hipster'],
      ['chourer', 'faucher'],
      ['déglingué', 'pété'],
    ],
    [L.ES]: [
      ['tío', 'colega'],
      ['tía', 'chica'],
      ['amigo', 'pana'],
      ['poli', 'madero'],
      ['pasta', 'guita'],
      ['pasta', 'dinero'],
      ['plata', 'lana'],
      ['coche', 'buga'],
      ['casa', 'chabola'],
      ['libro', 'tocho'],
      ['curro', 'tajo'],
      ['comida', 'papeo'],
      ['pitillo', 'cigarro'],
      ['beber', 'pimplar'],
      ['borrachera', 'pedo'],
      ['molar', 'flipar'],
      ['raro', 'chungo'],
      ['loco', 'pirado'],
      ['pesado', 'coñazo'],
      ['cabreado', 'mosqueado'],
      ['limpio', 'chuli'],
      ['genial', 'mortal'],
      ['bofetada', 'hostia'],
      ['dormir', 'sobar'],
      ['mirar', 'cotillear'],
      ['trabajar', 'currar'],
      ['vaguear', 'holgazanear'],
      ['robar', 'mangar'],
      ['ligar', 'pillar'],
      ['guay', 'chulo'],
      ['madre', 'vieja'],
      ['padre', 'viejo'],
      ['niño', 'chaval'],
      ['crío', 'mocoso'],
      ['matón', 'macarra'],
      ['resentido', 'amargado'],
      ['hola', 'qué pasa'],
      ['compi', 'hermano'],
      ['priva', 'alcohol'],
      ['fiesta', 'movida'],
      ['discoteca', 'antro'],
      ['pillado', 'cazado'],
      ['aburrido', 'harto'],
      ['cutre', 'chusca'],
      ['hortera', 'cursi'],
      ['listo', 'espabilado'],
      ['fanfarrón', 'chulito'],
      ['pijo', 'hipster'],
      ['mangar', 'chorizar'],
      ['machacado', 'destrozado'],
    ],
    [L.DE]: [
      ['Alter', 'Digga'],
      ['Tussi', 'Mädel'],
      ['Kumpel', 'Homie'],
      ['Bulle', 'Cop'],
      ['Kohle', 'Knete'],
      ['Knete', 'Mäuse'],
      ['Mäuse', 'Asche'],
      ['Karre', 'Kiste'],
      ['Bude', 'Hütte'],
      ['Buch', 'Schinken'],
      ['Job', 'Maloche'],
      ['Essen', 'Futter'],
      ['Kippe', 'Glimmstängel'],
      ['saufen', 'picheln'],
      ['Rausch', 'Suff'],
      ['lieben', 'mögen'],
      ['komisch', 'schräg'],
      ['verrückt', 'bekloppt'],
      ['nervig', 'ätzend'],
      ['sauer', 'angepisst'],
      ['cool', 'fett'],
      ['geil', 'krass'],
      ['Klatsche', 'Backpfeife'],
      ['pennen', 'dösen'],
      ['gucken', 'lugen'],
      ['arbeiten', 'schuften'],
      ['faulenzen', 'chillen'],
      ['klauen', 'mopsen'],
      ['flirten', 'anbaggern'],
      ['kapieren', 'checken'],
      ['Mama', 'Mutti'],
      ['Papa', 'Vati'],
      ['Kind', 'Gör'],
      ['Bengel', 'Lausbub'],
      ['Schläger', 'Gangster'],
      ['salzig', 'verbittert'],
      ['hi', 'na'],
      ['Bro', 'Kollege'],
      ['Schnaps', 'Hochprozentiges'],
      ['Party', 'Sause'],
      ['Club', 'Disko'],
      ['erwischt', 'geschnappt'],
      ['gelangweilt', 'genervt'],
      ['mies', 'lahm'],
      ['peinlich', 'kitschig'],
      ['clever', 'schlau'],
      ['Angeber', 'Wichtigtuer'],
      ['Schickimicki', 'Hipster'],
      ['stibitzen', 'abstauben'],
      ['fertig', 'hinüber'],
    ],
    [L.PT]: [
      ['meu', 'pá'],
      ['miúda', 'gaja'],
      ['amigo', 'brother'],
      ['polícia', 'chui'],
      ['massa', 'guita'],
      ['cacau', 'dinheiro'],
      ['nota', 'tuto'],
      ['carro', 'xexa'],
      ['casa', 'barraca'],
      ['livro', 'calhamaço'],
      ['trampo', 'trabalho'],
      ['comida', 'rango'],
      ['cigarro', 'queca'],
      ['beber', 'mamar'],
      ['bebedeira', 'pifo'],
      ['curtir', 'gramar'],
      ['estranho', 'esquisito'],
      ['doido', 'passado'],
      ['chato', 'seca'],
      ['zangado', 'irritado'],
      ['fixe', 'porreiro'],
      ['brutal', 'mortífero'],
      ['estalada', 'chapada'],
      ['dormir', 'dormitar'],
      ['olhar', 'espreitar'],
      ['trabalhar', 'mourejar'],
      ['mandriar', 'cangonhar'],
      ['roubar', 'surripiar'],
      ['paquerar', 'engatar'],
      ['esquisito', 'bizarro'],
      ['mãe', 'velha'],
      ['pai', 'velho'],
      ['miúdo', 'puto'],
      ['criança', 'fedelho'],
      ['bandido', 'marginal'],
      ['magoado', 'ressentido'],
      ['olá', 'ó pá'],
      ['mano', 'parça'],
      ['álcool', 'pinga'],
      ['festa', 'arraial'],
      ['discoteca', 'bar'],
      ['apanhado', 'pilhado'],
      ['aborrecido', 'farto'],
      ['mau', 'lixado'],
      ['piroso', 'cafona'],
      ['esperto', 'vivo'],
      ['bazófia', 'fanfarrão'],
      ['menino bem', 'hipster'],
      ['gamar', 'abafar'],
      ['acabado', 'arrasado'],
    ],
  },
};
